export type MaarifWorkflowSource = "generated" | "audited_upload";
export type MaarifQuestionDifficulty = "Kolay" | "Orta" | "Zor";
export const MAARIF_DIFFICULTY_OPTIONS: MaarifQuestionDifficulty[] = ["Kolay", "Orta", "Zor"];
export type MaarifQuestionLengthPreset = "Kısa" | "Orta" | "Uzun";
export const MAARIF_LENGTH_OPTIONS: Array<{
  value: MaarifQuestionLengthPreset;
  label: string;
  description: string;
}> = [
  { value: "Kısa", label: "Kısa", description: "Yaklaşık 60-90 kelime" },
  { value: "Orta", label: "Orta", description: "Yaklaşık 90-140 kelime" },
  { value: "Uzun", label: "Uzun", description: "Yaklaşık 140-220 kelime" },
];

export interface MaarifQuestionGenerationSettings {
  lengthPreset: MaarifQuestionLengthPreset;
  targetWordCount?: number | null;
  wantsVisual?: boolean;
  customPrompt?: string | null;
}

export interface MaarifProcessComponent {
  code: string;
  label: string;
  group: "Problem Çözme" | "Sorgulama";
}

export const MAARIF_PROCESS_COMPONENTS: MaarifProcessComponent[] = [
  { code: "KB3.2.SB1", label: "Problemi yapılandırmak", group: "Problem Çözme" },
  { code: "KB3.2.SB2", label: "Problemi özetlemek", group: "Problem Çözme" },
  { code: "KB3.2.SB3", label: "Gözleme/veriye dayalı tahmin", group: "Problem Çözme" },
  { code: "KB3.2.SB4", label: "Akıl yürütme", group: "Problem Çözme" },
  { code: "KB3.2.SB5", label: "Yansıtma/değerlendirme", group: "Problem Çözme" },
  { code: "KB2.8.SB1", label: "Konuyu tanımlamak", group: "Sorgulama" },
  { code: "KB2.8.SB2", label: "Sorular sormak", group: "Sorgulama" },
  { code: "KB2.8.SB3", label: "Bilgi toplamak", group: "Sorgulama" },
  { code: "KB2.8.SB4", label: "Doğruluğu değerlendirmek", group: "Sorgulama" },
  { code: "KB2.8.SB5", label: "Çıkarım yapmak", group: "Sorgulama" },
];

export const MAARIF_GRADE_OPTIONS = Array.from({ length: 12 }, (_, index) => `${index + 1}. Sınıf`);

export interface MaarifQuestionContextPayload extends MaarifQuestionGenerationSettings {
  lessonId: string;
  gradeLabel: string;
  gainText: string;
  processComponentCode: string;
  difficulty: MaarifQuestionDifficulty;
}

export interface MaarifQuestionContextResponse extends MaarifQuestionContextPayload {
  lessonTitle: string;
  processComponentLabel: string;
  context: string;
  rationale: string;
  model: string;
}

export interface MaarifQuestionImageAsset {
  mimeType: string;
  base64: string;
  prompt?: string;
  title?: string;
  caption?: string;
  trimBottomRatio?: number;
}

export interface MaarifQuestionValidationWarning {
  type: string;
  reason: string;
  sourceEvidence?: string;
  generatedEvidence?: string;
  choiceKeys?: Array<"A" | "B" | "C" | "D" | "E">;
}

export interface MaarifSingleQuestion {
  measuredProcessComponentCode: string;
  measuredProcessComponentLabel: string;
  gainText?: string | null;
  sourcePageNumber?: number | null;
  context: string;
  questionStem: string;
  choices: Record<"A" | "B" | "C" | "D" | "E", string>;
  correctAnswer: "A" | "B" | "C" | "D" | "E" | null;
  shortJustification: string;
  distractorAnalysis: Record<"A" | "B" | "C" | "D" | "E", string>;
  image?: MaarifQuestionImageAsset | null;
  sourceReferenceImage?: MaarifQuestionImageAsset | null;
  bodyImages?: MaarifQuestionImageAsset[] | null;
  images?: MaarifQuestionImageAsset[] | null;
  supportImages?: MaarifQuestionImageAsset[] | null;
  choiceImages?: Partial<Record<"A" | "B" | "C" | "D" | "E", MaarifQuestionImageAsset | null>> | null;
  reviewStatus?: "needs_review" | "ready" | string | null;
  needsReview?: boolean;
  validationWarnings?: MaarifQuestionValidationWarning[];
}

export interface MaarifQuestionGeneratePayload extends MaarifQuestionContextPayload {
  context: string;
  customPrompt?: string | null;
}

export interface MaarifQuestionGenerateResponse extends MaarifQuestionContextPayload {
  lessonTitle: string;
  question: MaarifSingleQuestion;
  model: string;
  imageModel?: string | null;
  imageError?: string | null;
}

export interface MaarifQuestionSourceAnalysis {
  originalQuestionSummary: string;
  strengths: string[];
  weaknesses: string[];
  improvementFocus: string[];
  howWeImproved: string[];
  detectedVisualNeed: boolean;
  visualRationale: string;
}

export interface MaarifQuestionDetectedMetadata {
  lessonId?: string | null;
  lessonTitle?: string | null;
  gradeLabel?: string | null;
  gainText?: string | null;
  processComponentCode?: string | null;
  processComponentLabel?: string | null;
}

export interface MaarifQuestionGenerateFromImagePayload extends MaarifQuestionContextPayload {
  context?: string;
  image: File;
  customPrompt?: string | null;
}

export interface MaarifQuestionGenerateFromImageResponse extends MaarifQuestionContextPayload {
  lessonTitle: string;
  processComponentLabel: string;
  detectedMetadata?: MaarifQuestionDetectedMetadata | null;
  question: MaarifSingleQuestion;
  analysis: MaarifQuestionSourceAnalysis;
  model: string;
  imageModel?: string | null;
  imageError?: string | null;
}

export interface MaarifQuestionAuditReport {
  summary: string;
  alignmentVerdict: string;
  strengths: string[];
  weaknesses: string[];
  improvementSuggestions: string[];
  originalQuestionSummary: string;
}

export interface MaarifQuestionAuditPayload extends MaarifQuestionContextPayload {
  questionText?: string;
  image?: File | null;
  customPrompt?: string | null;
}

export interface MaarifQuestionAuditResponse extends MaarifQuestionContextPayload {
  lessonTitle: string;
  processComponentLabel: string;
  report: MaarifQuestionAuditReport;
  model: string;
}

export interface MaarifQuestionRewritePayload extends MaarifQuestionContextPayload {
  questionText?: string;
  context?: string;
  image?: File | null;
  auditReport: MaarifQuestionAuditReport;
  customPrompt?: string | null;
}

export type MaarifPdfRewriteMode = "surface" | "contextual";

export interface MaarifQuestionGenerateFromPdfPayload extends MaarifQuestionGenerationSettings {
  difficulty: MaarifQuestionDifficulty;
  rewriteMode: MaarifPdfRewriteMode;
  context?: string;
  selectedPages?: number[];
  pdf: File;
}

export interface MaarifQuestionPdfMetadataResponse {
  fileName: string;
  pageCount: number;
}

export interface MaarifQuestionPdfPreviewItem {
  pageNumber: number;
  mimeType: string;
  dataUrl: string;
}

export interface MaarifQuestionPdfPreviewsResponse {
  fileName: string;
  pageCount: number;
  previews: MaarifQuestionPdfPreviewItem[];
}

export interface MaarifQuestionGenerateFromPdfResponse extends MaarifQuestionGenerationSettings {
  difficulty: MaarifQuestionDifficulty;
  rewriteMode: MaarifPdfRewriteMode;
  sourceFileName: string;
  selectedPages?: number[];
  detectedMetadata?: MaarifQuestionDetectedMetadata | null;
  summary?: {
    questionCount: number;
    needsReviewQuestionCount?: number;
    notes?: string;
  } | null;
  questions: MaarifSingleQuestion[];
  model: string;
}

export type MaarifQuestionPdfJobStatus = "queued" | "processing" | "completed" | "failed";
export type MaarifQuestionPdfJobPhase =
  | "queued"
  | "preparing_pdf"
  | "counting_questions"
  | "generating_questions"
  | "validating_questions"
  | "finalizing"
  | "completed"
  | "failed";

export interface MaarifQuestionPdfJobResponse {
  id: string;
  status: MaarifQuestionPdfJobStatus;
  phase: MaarifQuestionPdfJobPhase;
  message: string;
  createdAt: string;
  updatedAt?: string;
  startedAt: string | null;
  finishedAt: string | null;
  requestedBy: {
    id: string;
    fullName: string;
    email?: string | null;
  };
  sourceFile: {
    name: string;
    size?: number | null;
  };
  rewriteMode: MaarifPdfRewriteMode;
  progress: {
    totalSteps: number;
    currentStep: number;
    percentage: number;
  };
  diagnostics?: {
    activeOperations?: string[];
    activeOperationCount?: number;
    completedBatches?: number;
    totalBatches?: number;
    lastActivityAt?: string | null;
  } | null;
  result: MaarifQuestionGenerateFromPdfResponse | null;
  error: string | null;
}

export interface MaarifQuestionRewriteResponse extends MaarifQuestionContextPayload {
  lessonTitle: string;
  question: MaarifSingleQuestion;
  model: string;
}

export interface MaarifQuestionBankExportInfo {
  questionBankId: string | null;
  exportedAt?: string | null;
}

export interface MaarifQuestionToQuestionBankPayload {
  recordId?: string | null;
  title: string;
  lessonTitle?: string | null;
  questionBankId?: string | null;
  content: Pick<MaarifQuestionRecordContent, "question" | "multiQuestions" | "detectedMetadata" | "rewriteQuestion">;
}

export interface MaarifQuestionToQuestionBankResponse {
  questionBankId: string;
  questionBankTitle: string | null;
  questionCount: number;
  appendedCount: number;
  exportedAt: string;
}

export interface MaarifExportTarget {
  id: string;
  testAdi: string;
  kategori: string | null;
  questionCount: number;
  updatedAt: string;
}

export interface MaarifExportTargetListResponse {
  count: number;
  rows: MaarifExportTarget[];
}

export interface MaarifQuestionRecordContent {
  generationSettings?: MaarifQuestionGenerationSettings | null;
  context: {
    text?: string;
    rationale?: string;
  } | null;
  question: MaarifSingleQuestion | null;
  multiQuestions?: MaarifSingleQuestion[] | null;
  detectedMetadata?: MaarifQuestionDetectedMetadata | null;
  questionSourceAnalysis?: MaarifQuestionSourceAnalysis | null;
  auditReport: MaarifQuestionAuditReport | null;
  rewriteQuestion: MaarifSingleQuestion | null;
  questionBankExport?: MaarifQuestionBankExportInfo | null;
  sourceAsset?: {
    type: "image" | "pdf";
    fileName?: string | null;
    selectedPages?: number[] | null;
    transformMode?: MaarifPdfRewriteMode | null;
  } | null;
  modelInfo: {
    contextModel?: string | null;
    questionModel?: string | null;
    questionFromImageModel?: string | null;
    questionsFromPdfModel?: string | null;
    questionImageModel?: string | null;
    auditModel?: string | null;
    rewriteModel?: string | null;
  } | null;
}

export interface MaarifQuestionRecord {
  id: string;
  userId: string;
  title: string;
  lessonId: string | null;
  lessonTitle: string | null;
  gradeLabel: string | null;
  difficulty: MaarifQuestionDifficulty;
  gainText: string | null;
  processComponentCode: string | null;
  processComponentLabel: string | null;
  workflowSource: MaarifWorkflowSource;
  content: MaarifQuestionRecordContent;
  createdAt: string;
  updatedAt: string;
}

export interface MaarifQuestionRecordSavePayload {
  title: string;
  lessonId: string | null;
  lessonTitle: string | null;
  gradeLabel: string | null;
  difficulty: MaarifQuestionDifficulty;
  gainText: string | null;
  processComponentCode: string | null;
  workflowSource: MaarifWorkflowSource;
  content: MaarifQuestionRecordContent;
}

export type MaarifQuestionRecordUpdatePayload = Partial<MaarifQuestionRecordSavePayload>;

export interface MaarifQuestionRecordListResponse {
  count: number;
  rows: MaarifQuestionRecord[];
}

export interface MaarifPromptConfig {
  key: string;
  label: string;
  description: string;
  defaultContent: string;
  content: string | null;
  isCustomized: boolean;
}
